import { Injectable, signal } from '@angular/core';
import { BUNDLED_EN_TRANSLATIONS } from '@core/i18n/bundled-en-translations';
import { TranslateService } from '@ngx-translate/core';
import { TimeagoIntl } from 'ngx-timeago';
import { strings as timeagoDeStrings } from 'ngx-timeago/language-strings/de.js';
import { strings as timeagoEnStrings } from 'ngx-timeago/language-strings/en.js';
import { firstValueFrom } from 'rxjs';
import { ELanguage } from '../../app.interface';

const LANGUAGE_STORAGE_KEY = 'language';
const SHOW_LOADING_INDICATOR_DELAY_MS = 1000;
const GIVE_UP_TIMEOUT_MS = 10000;

/**
 * Boots the app's translations without ever blocking Angular's bootstrap on a network call.
 *
 * English is baked into the JS bundle (see BUNDLED_EN_TRANSLATIONS) and is always available
 * instantly - it's both ngx-translate's default/fallback language and the one we render if
 * the user's actually-selected language doesn't arrive in time.
 *
 * `ready` gates whether AppComponent shows real content. `showLoadingIndicator` only flips on
 * if nothing has rendered within SHOW_LOADING_INDICATOR_DELAY_MS, so a fast, healthy load
 * never flashes a loading state at all. If the selected language is still not ready after
 * GIVE_UP_TIMEOUT_MS, we render in English rather than staying blank - but if that language
 * shows up later anyway, we still switch to it. That's the one case where the visible
 * language changes after render: only once we've already given up on it arriving in time.
 */
@Injectable({
  providedIn: 'root',
})
export class TranslationBootService {
  public readonly ready = signal(false);
  public readonly showLoadingIndicator = signal(false);

  private hasGivenUp = false;

  constructor(
    private readonly translate: TranslateService,
    private readonly timeagoIntl: TimeagoIntl,
  ) {
    this.start();
  }

  private start(): void {
    this.syncTimeagoLanguage();
    this.translate.setTranslation(ELanguage.en, BUNDLED_EN_TRANSLATIONS, true);
    this.translate.setDefaultLang(ELanguage.en);

    const initialLanguage = this.getInitialLanguage();

    if (initialLanguage === ELanguage.en) {
      // Already bundled - nothing to wait for.
      this.translate.use(ELanguage.en);
      this.ready.set(true);

      return;
    }

    this.loadSelectedLanguage(initialLanguage);
  }

  private loadSelectedLanguage(language: ELanguage): void {
    const showLoadingTimer = setTimeout(() => {
      if (!this.ready()) {
        this.showLoadingIndicator.set(true);
      }
    }, SHOW_LOADING_INDICATOR_DELAY_MS);

    const giveUpTimer = setTimeout(() => {
      this.fallBackToEnglish();
    }, GIVE_UP_TIMEOUT_MS);

    firstValueFrom(this.translate.use(language)).then(
      () => {
        clearTimeout(showLoadingTimer);
        clearTimeout(giveUpTimer);

        if (this.hasGivenUp) {
          // ngx-translate's use()/changeLang() only applies a load for whichever language was
          // MOST RECENTLY requested (see this.lastUseLanguage in its source) - fallBackToEnglish()
          // already called use(en) after this language's own use() call, so even though this
          // load just succeeded, changeLang() would otherwise silently ignore it as stale.
          // Calling use() again resolves instantly from the now-cached translations and
          // re-establishes it as the most recent call, so it actually takes effect this time.
          this.translate.use(language);
        }

        this.ready.set(true);
      },
      () => {
        if (!this.hasGivenUp) {
          clearTimeout(showLoadingTimer);
          clearTimeout(giveUpTimer);
          this.fallBackToEnglish();
        }
      },
    );
  }

  private fallBackToEnglish(): void {
    this.hasGivenUp = true;
    this.translate.use(ELanguage.en);
    this.ready.set(true);
  }

  // Relative times ("3 days ago") come from ngx-timeago, not ngx-translate - keep its strings on the
  // UI language. English is set up front so nothing ever renders without strings.
  private syncTimeagoLanguage(): void {
    this.timeagoIntl.strings = timeagoEnStrings;

    this.translate.onLangChange.subscribe(({ lang }) => {
      this.timeagoIntl.strings = (lang as ELanguage) === ELanguage.de ? timeagoDeStrings : timeagoEnStrings;
      this.timeagoIntl.changes.next();
    });
  }

  private getInitialLanguage(): ELanguage {
    let storedLanguage: string | null = null;

    // Runs at boot: blocked storage (privacy settings) must not keep the app from rendering.
    try {
      storedLanguage = typeof window !== 'undefined' ? localStorage.getItem(LANGUAGE_STORAGE_KEY) : null;
    } catch {
      /* Ignore storage errors - English it is */
    }

    return storedLanguage === ELanguage.de || storedLanguage === ELanguage.en ? storedLanguage : ELanguage.en;
  }
}
